import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { CalendarBlank, Plus, Trash, Confetti } from "@phosphor-icons/react";

// Sundays don't get stored server-side — they are always weekly-off. We
// enumerate them on the client here so the calendar shows a complete
// non-working-days list for the year. This keeps the DB tiny while still
// giving managers one place to see "here's every off-day for 2026".
function sundaysOf(year) {
  const out = [];
  const first = new Date(Date.UTC(year, 0, 1));
  // Advance to the first Sunday
  const offset = (7 - first.getUTCDay()) % 7;
  first.setUTCDate(first.getUTCDate() + offset);
  while (first.getUTCFullYear() === year) {
    out.push(first.toISOString().slice(0, 10));
    first.setUTCDate(first.getUTCDate() + 7);
  }
  return out;
}

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

export default function HrHolidaysPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ date: "", name: "", note: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hr/holidays", { params: { year } });
      setRows(data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  // Union of stored public holidays + computed Sundays, sorted by date.
  const allNonWorkingDays = useMemo(() => {
    const suns = sundaysOf(year).map((d) => ({
      id: `sun-${d}`, date: d, name: "Weekly Off (Sunday)", is_sunday: true,
    }));
    const combined = [...rows.map((r) => ({ ...r, is_sunday: false })), ...suns];
    combined.sort((a, b) => a.date.localeCompare(b.date));
    return combined;
  }, [rows, year]);

  const grouped = useMemo(() => {
    const m = {};
    allNonWorkingDays.forEach((d) => {
      const mo = parseInt(d.date.slice(5, 7), 10) - 1;
      m[mo] = m[mo] || [];
      m[mo].push(d);
    });
    return m;
  }, [allNonWorkingDays]);

  const addHoliday = async (e) => {
    e.preventDefault();
    if (!form.date || !form.name.trim()) {
      toast.error("Date and name are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/hr/holidays", form);
      toast.success(`Added: ${form.name} (${form.date})`);
      setForm({ date: "", name: "", note: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const removeHoliday = async (row) => {
    if (row.is_sunday) return; // Sundays cannot be removed
    if (!window.confirm(`Delete holiday "${row.name}" on ${row.date}?`)) return;
    try {
      await api.delete(`/hr/holidays/${row.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const publicHolidayCount = rows.length;
  const sundayCount = sundaysOf(year).length;

  return (
    <div>
      <PageHeader
        eyebrow="HR / 04"
        title="Holiday Calendar"
        subtitle="Company-wide non-working days for the year. Sundays are automatic. Add public holidays (Diwali, Republic Day, etc.) here — Attendance grid will default these dates to Weekly Off, and any Present mark auto-records as OT."
        actions={
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 border border-canvas-line px-3 py-1.5 bg-canvas">
              <CalendarBlank size={13} className="text-ink-muted" />
              <select
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value, 10))}
                data-testid="hr-holidays-year"
                className="bg-transparent text-sm focus:outline-none"
              >
                {[year - 1, year, year + 1, year + 2].map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
        }
      />

      <div className="px-8 py-6 space-y-6">
        {/* Counts strip */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="border border-canvas-line px-4 py-3 bg-canvas-card">
            <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Public Holidays</div>
            <div className="font-display text-2xl font-bold tabular-nums mt-1" data-testid="hr-holidays-count-public">{publicHolidayCount}</div>
          </div>
          <div className="border border-canvas-line px-4 py-3 bg-canvas-card">
            <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Sundays</div>
            <div className="font-display text-2xl font-bold tabular-nums mt-1" data-testid="hr-holidays-count-sundays">{sundayCount}</div>
          </div>
          <div className="border border-canvas-line px-4 py-3 bg-canvas-card">
            <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Total Off Days</div>
            <div className="font-display text-2xl font-bold tabular-nums mt-1" data-testid="hr-holidays-count-total">{publicHolidayCount + sundayCount}</div>
          </div>
        </div>

        {/* Add form */}
        <form onSubmit={addHoliday} className="border border-canvas-line bg-canvas-card p-4" data-testid="hr-holidays-add-form">
          <div className="text-[10px] uppercase tracking-widest text-ink-muted font-mono mb-3">Add Public Holiday</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              data-testid="hr-holidays-date"
              className="px-3 py-2 bg-canvas border border-canvas-line text-sm"
              required
            />
            <input
              type="text"
              placeholder="Name (e.g. Diwali)"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="hr-holidays-name"
              className="px-3 py-2 bg-canvas border border-canvas-line text-sm"
              required
            />
            <input
              type="text"
              placeholder="Note (optional)"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              data-testid="hr-holidays-note"
              className="px-3 py-2 bg-canvas border border-canvas-line text-sm"
            />
            <button
              type="submit"
              disabled={saving}
              data-testid="hr-holidays-add-btn"
              className="bg-ink text-white px-4 py-2 text-sm font-semibold hover:bg-brand disabled:opacity-40 flex items-center justify-center gap-2"
            >
              <Plus size={13} weight="bold" />
              {saving ? "Adding…" : "Add Holiday"}
            </button>
          </div>
        </form>

        {/* Month grid */}
        {loading && (
          <div className="text-center font-mono text-xs text-ink-subtle py-8">Loading…</div>
        )}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="hr-holidays-months">
            {MONTH_NAMES.map((mn, mi) => (
              <div key={mi} className="border border-canvas-line bg-canvas-card">
                <div className="px-4 py-2 bg-canvas-panel/50 border-b border-canvas-line flex items-center justify-between">
                  <div className="font-semibold text-sm">{mn} {year}</div>
                  <div className="font-mono text-[10px] text-ink-muted">{(grouped[mi] || []).length} days</div>
                </div>
                <ul className="p-2 min-h-[80px]">
                  {(grouped[mi] || []).length === 0 && (
                    <li className="text-center font-mono text-[10px] text-ink-subtle py-4">—</li>
                  )}
                  {(grouped[mi] || []).map((d) => (
                    <li
                      key={d.id}
                      data-testid={`hr-holiday-row-${d.date}`}
                      className={`flex items-center gap-2 py-1.5 px-2 text-sm border-b border-canvas-line/40 last:border-0 ${
                        d.is_sunday ? "text-ink-muted" : ""
                      }`}
                    >
                      <span className="font-mono text-[11px] tabular-nums w-16">{d.date.slice(8)}</span>
                      <span className="flex-1 flex items-center gap-1.5">
                        {!d.is_sunday && <Confetti size={11} weight="fill" className="text-amber-500" />}
                        {d.name}
                      </span>
                      {!d.is_sunday && (
                        <button
                          onClick={() => removeHoliday(d)}
                          data-testid={`hr-holiday-delete-${d.date}`}
                          className="p-1 hover:text-destructive"
                          title="Delete"
                        >
                          <Trash size={12} weight="bold" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
